import { useEffect, useState } from 'react';
import {
  Table,
  Typography,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Descriptions,
  Divider,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, EyeOutlined, EnvironmentOutlined } from '@ant-design/icons';
import {
  supabase,
  getSignedUrl,
  formatDate,
  formatDateTime,
  parseGeoPoint,
  useAuthUser,
  mapSOSPriority,
  sosPriorityColor,
  sosPriorityLabel,
  recommendRescueVehicles,
  formatDistance,
  usePortalPermissions,
  defaultEndTimeFromPickup,
} from '@ubus/shared';
import type { SOSEvent, Trip, RescueVehicleRecommendation } from '@ubus/shared';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import EntityDrawer from '../components/EntityDrawer';
import AdminListPage from '../components/AdminListPage';
import StatusTag from '../components/StatusTag';
import { TableRowActions } from '../components/TableRowActions';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import { formatTripBrief } from '../utils/formatTripBrief';

const { Text, Link } = Typography;

type TripLabelRow = {
  id: string;
  date?: string | null;
  pickup_time?: string | null;
  pickup_address?: string | null;
  organizations?: { name?: string | null } | null;
};

function SOSEvents() {
  const { t } = useTranslation();
  const user = useAuthUser();
  const { hasPermission } = usePortalPermissions();
  const canHandle = hasPermission('sos.handle');
  const [events, setEvents] = useState<SOSEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [handleModalVisible, setHandleModalVisible] = useState(false);
  const [handlingEvent, setHandlingEvent] = useState<SOSEvent | null>(null);
  const [detailEvent, setDetailEvent] = useState<SOSEvent | null>(null);
  const [detailTrip, setDetailTrip] = useState<Trip | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [form] = Form.useForm();
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [detailPhotoUrl, setDetailPhotoUrl] = useState<string | null>(null);
  const [rescueVehicles, setRescueVehicles] = useState<RescueVehicleRecommendation[]>([]);
  const [activeDrivers, setActiveDrivers] = useState<{ id: string; name: string }[]>([]);
  const [dispatchVehicleId, setDispatchVehicleId] = useState<string | undefined>();
  const [dispatchDriverId, setDispatchDriverId] = useState<string | undefined>();
  const [dispatching, setDispatching] = useState(false);
  const [tripLabels, setTripLabels] = useState<Record<string, string>>({});
  const { pagination, onTableChange, rowClassName, afterMutation } = useAdminTableStability();

  useEffect(() => {
    fetchEvents();
    supabase
      .from('drivers')
      .select('id, name')
      .eq('status', 'active')
      .order('name')
      .then(({ data }) => setActiveDrivers(data || []));
  }, [statusFilter]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const nextUrls: Record<string, string> = {};

      await Promise.all(
        events.map(async (event) => {
          if (!event.photo_url) return;

          if (event.photo_url.startsWith('http')) {
            nextUrls[event.id] = event.photo_url;
            return;
          }

          const signed = await getSignedUrl('sos', event.photo_url, 3600);
          if (signed) nextUrls[event.id] = signed;
        })
      );

      if (!cancelled) {
        setPhotoUrls(nextUrls);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [events]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('sos_events')
        .select('*')
        .order('created_at', { ascending: false });

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) {
        message.error(error.message);
        setEvents([]);
        setTripLabels({});
        return;
      }

      const rows = data || [];
      setEvents(rows);

      const tripIds = [...new Set(rows.map((e) => e.trip_id).filter(Boolean))] as string[];
      if (tripIds.length === 0) {
        setTripLabels({});
        return;
      }

      const { data: tripRows } = await supabase
        .from('trips')
        .select('id, date, pickup_time, pickup_address, organizations(name)')
        .in('id', tripIds);

      const labels: Record<string, string> = {};
      for (const t of (tripRows || []) as TripLabelRow[]) {
        labels[t.id] = formatTripBrief(t);
      }
      setTripLabels(labels);
    } finally {
      setLoading(false);
    }
  };

  const formatHandler = (uid?: string) => (uid ? t('sos.assigned') : '—');

  const getEventPriority = (event: SOSEvent) =>
    event.priority ?? mapSOSPriority(event.event_type);

  const openDetail = async (record: SOSEvent) => {
    setDetailEvent(record);
    setDetailTrip(null);
    setDetailPhotoUrl(null);
    setRescueVehicles([]);
    setDispatchVehicleId(undefined);
    setDispatchDriverId(undefined);
    setDetailLoading(true);

    try {
      const [tripResult, rescueResult] = await Promise.all([
        record.trip_id
          ? supabase
              .from('trips')
              .select('*, organizations(name), drivers(name), vehicles(fleet_number)')
              .eq('id', record.trip_id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        recommendRescueVehicles(record.id).catch(() => [] as RescueVehicleRecommendation[]),
      ]);

      if (tripResult.data) {
        setDetailTrip(tripResult.data as Trip | null);
      }
      setRescueVehicles(rescueResult.slice(0, 3));

      if (record.photo_url) {
        if (record.photo_url.startsWith('http')) {
          setDetailPhotoUrl(record.photo_url);
        } else {
          const signed = await getSignedUrl('sos', record.photo_url, 3600);
          setDetailPhotoUrl(signed);
        }
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDispatchRescue = async () => {
    if (!detailEvent || !dispatchVehicleId || !dispatchDriverId) {
      message.warning(t('sos.selectVehicleDriver'));
      return;
    }
    if (!detailTrip?.org_id) {
      message.error(t('sos.missingOrganization'));
      return;
    }

    setDispatching(true);
    try {
      const pickupTime = dayjs().add(30, 'minute').format('HH:mm:ss');
      const today = dayjs().format('YYYY-MM-DD');
      // trips.end_time is NOT NULL — default +2h (same as HQ/Owner trip forms)
      const endTime = defaultEndTimeFromPickup(pickupTime);

      const { error } = await supabase.from('trips').insert([
        {
          org_id: detailTrip.org_id,
          date: today,
          pickup_time: pickupTime,
          end_time: endTime,
          pickup_address: detailTrip.pickup_address,
          dropoff_address: detailTrip.dropoff_address,
          pickup_geopoint: detailTrip.pickup_geopoint,
          dropoff_geopoint: detailTrip.dropoff_geopoint,
          passenger_count: detailTrip.passenger_count ?? 1,
          special_notes: 'Emergency rescue dispatch from SOS alert',
          status: 'dispatched',
          vehicle_id: dispatchVehicleId,
          driver_id: dispatchDriverId,
        },
      ]);

      if (error) {
        message.error(error.message);
      } else {
        message.success(t('sos.rescueDispatched'));
        setDetailEvent(null);
      }
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('sos.dispatchFailed'));
    } finally {
      setDispatching(false);
    }
  };

  const handleOpenHandle = (record: SOSEvent) => {
    setHandlingEvent(record);
    form.resetFields();
    setHandleModalVisible(true);
  };

  const handleSubmit = async (values: {
    handling_method: string;
    handling_notes?: string;
    follow_up?: string;
  }) => {
    if (!handlingEvent || !user) return;

    try {
      const { error } = await supabase
        .from('sos_events')
        .update({
          status: 'resolved',
          handled_by: user.id,
          handling_method: values.handling_method,
          handling_notes: values.handling_notes,
          follow_up: values.follow_up,
        })
        .eq('id', handlingEvent.id);

      if (error) {
        message.error(error.message);
      } else {
        const resolvedId = handlingEvent.id;
        message.success(t('sos.resolvedSuccess'));
        setHandleModalVisible(false);
        await afterMutation(resolvedId, fetchEvents);
      }
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('sos.updateFailed'));
    }
  };

  const gpsLink = (location?: string) => {
    const point = parseGeoPoint(location ?? null);
    if (!point) return null;
    return `https://www.google.com/maps?q=${point.lat},${point.lng}`;
  };

  const columns: TableProps<SOSEvent>['columns'] = [
    {
      title: t('sos.columns.eventType'),
      dataIndex: 'event_type',
      key: 'event_type',
      width: 140,
      fixed: 'left',
      ellipsis: true,
      sorter: (a, b) => a.event_type.localeCompare(b.event_type),
    },
    {
      title: t('sos.columns.priority'),
      key: 'priority',
      width: 100,
      render: (_, record) => {
        const priority = getEventPriority(record);
        return (
          <Tag color={sosPriorityColor(priority)}>{priority}</Tag>
        );
      },
    },
    {
      title: t('sos.columns.trip'),
      dataIndex: 'trip_id',
      key: 'trip_id',
      width: 220,
      ellipsis: true,
      render: (tripId: string) =>
        tripId ? tripLabels[tripId] || t('sos.linkedTrip') : '—',
    },
    {
      title: t('sos.columns.reporterRole'),
      dataIndex: 'reporter_role',
      key: 'reporter_role',
      width: 110,
      render: (role: string) => (
        <Tag color={role === 'driver' ? 'blue' : 'green'}>{role.toUpperCase()}</Tag>
      ),
    },
    {
      title: t('sos.columns.photo'),
      key: 'photo',
      width: 80,
      render: (_, record) =>
        photoUrls[record.id] ? (
          <img
            src={photoUrls[record.id]}
            alt={t('sos.title')}
            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
          />
        ) : record.photo_url ? (
          <Tag>{t('sos.loading')}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('sos.columns.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('sos.columns.handler'),
      dataIndex: 'handled_by',
      key: 'handled_by',
      width: 140,
      render: (uid: string | undefined) => formatHandler(uid),
    },
    {
      title: t('sos.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => <StatusTag status={status} />,
    },
    {
      title: t('sos.columns.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (val: string) => formatDateTime(val),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: t('sos.columns.updatedAt'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      render: (val: string) => formatDateTime(val),
      sorter: (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
    },
    {
      title: t('sos.columns.actions'),
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <TableRowActions>
          <Button type="link" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
            {t('sos.view')}
          </Button>
          {record.status !== 'resolved' && canHandle && (
            <Button type="link" icon={<EditOutlined />} onClick={() => handleOpenHandle(record)}>
              {t('sos.handle')}
            </Button>
          )}
        </TableRowActions>
      ),
    },
  ];

  const detailGps = detailEvent ? gpsLink(detailEvent.location) : null;

  return (
    <>
      <AdminListPage
        title={t('sos.title')}
        subtitle={t('sos.subtitle')}
        filters={
          <Select
            placeholder={t('sos.allStatuses')}
            allowClear
            style={{ minWidth: 180 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'new', label: t('sos.new') },
              { value: 'handling', label: t('sos.handling') },
              { value: 'resolved', label: t('sos.resolved') },
            ]}
          />
        }
        resultCount={events.length}
        hasActiveFilters={!!statusFilter}
        onClearFilters={() => setStatusFilter(undefined)}
      >
        <Table
          dataSource={events}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onChange={onTableChange}
          rowClassName={rowClassName}
          scroll={{ x: 1500 }}
          locale={{ emptyText: t('sos.noEvents') }}
        />
      </AdminListPage>

      <Modal
        title={t('sos.handleModal.title')}
        open={handleModalVisible}
        onCancel={() => setHandleModalVisible(false)}
        onOk={() => form.submit()}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="handling_method"
            label={t('sos.handleModal.method')}
            rules={[{ required: true, message: t('sos.handleModal.methodRequired') }]}
          >
            <Select placeholder={t('sos.handleModal.selectMethod')}>
              {([
                ['Phone', 'phone'],
                ['Email', 'email'],
                ['On-site', 'onSite'],
                ['Other', 'other'],
              ] as const).map(([value, method]) => (
                <Select.Option key={value} value={value}>
                  {t(`sos.handleModal.${method}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="handling_notes" label={t('sos.handleModal.notes')}>
            <Input.TextArea rows={4} placeholder={t('sos.handleModal.notesPlaceholder')} />
          </Form.Item>
          <Form.Item name="follow_up" label={t('sos.handleModal.followUp')}>
            <Input.TextArea rows={2} placeholder={t('sos.handleModal.followUpPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      <EntityDrawer
        open={!!detailEvent}
        onClose={() => setDetailEvent(null)}
        title={t('sos.details.title')}
        loading={detailLoading}
        width={640}
      >
        {detailEvent && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={t('sos.details.eventType')}>{detailEvent.event_type}</Descriptions.Item>
              <Descriptions.Item label={t('sos.details.priority')}>
                <Tag color={sosPriorityColor(getEventPriority(detailEvent))}>
                  {sosPriorityLabel(getEventPriority(detailEvent))}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('sos.details.reporterRole')}>
                <Tag color={detailEvent.reporter_role === 'driver' ? 'blue' : 'green'}>
                  {detailEvent.reporter_role.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('sos.details.contact')}>
                {detailEvent.contact_phone || t('sos.details.noPhone')}
              </Descriptions.Item>
              <Descriptions.Item label={t('sos.details.description')}>{detailEvent.description || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('sos.details.status')}>
                <StatusTag status={detailEvent.status} />
              </Descriptions.Item>
              <Descriptions.Item label={t('sos.details.created')}>{formatDateTime(detailEvent.created_at)}</Descriptions.Item>
              <Descriptions.Item label={t('sos.details.updated')}>{formatDateTime(detailEvent.updated_at)}</Descriptions.Item>
              {detailGps && (
                <Descriptions.Item label={t('sos.details.location')}>
                  <Link href={detailGps} target="_blank">
                    <EnvironmentOutlined /> {t('sos.details.openMaps')}
                  </Link>
                </Descriptions.Item>
              )}
              {detailTrip && (
                <>
                  <Descriptions.Item label={t('sos.details.trip')}>
                    {formatTripBrief(
                      detailTrip as Trip & { organizations?: { name: string } }
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sos.details.organization')}>
                    {(detailTrip as Trip & { organizations?: { name: string } }).organizations?.name || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sos.details.driver')}>
                    {(detailTrip as Trip & { drivers?: { name: string } }).drivers?.name || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sos.details.vehicle')}>
                    {(detailTrip as Trip & { vehicles?: { fleet_number: string } }).vehicles?.fleet_number || '—'}
                  </Descriptions.Item>
                </>
              )}
              {detailEvent.handled_by && (
                <Descriptions.Item label={t('sos.details.handledBy')}>
                  {formatHandler(detailEvent.handled_by)}
                </Descriptions.Item>
              )}
              {detailEvent.handling_method && (
                <Descriptions.Item label={t('sos.details.handlingMethod')}>{detailEvent.handling_method}</Descriptions.Item>
              )}
              {detailEvent.handling_notes && (
                <Descriptions.Item label={t('sos.details.handlingNotes')}>{detailEvent.handling_notes}</Descriptions.Item>
              )}
              {detailEvent.follow_up && (
                <Descriptions.Item label={t('sos.details.followUp')}>{detailEvent.follow_up}</Descriptions.Item>
              )}
            </Descriptions>
            {detailPhotoUrl && (
              <img
                src={detailPhotoUrl}
                alt={t('sos.title')}
                style={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 8, marginTop: 16 }}
              />
            )}

            {rescueVehicles.length > 0 && (
              <>
                <Divider orientation="left">{t('sos.details.recommendedVehicles')}</Divider>
                <Table
                  dataSource={rescueVehicles}
                  rowKey="vehicle_id"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: t('sos.details.fleetNumber'), dataIndex: 'fleet_number', key: 'fleet_number' },
                    { title: t('sos.details.model'), dataIndex: 'model', key: 'model' },
                    { title: t('sos.details.capacity'), dataIndex: 'capacity', key: 'capacity' },
                    {
                      title: t('sos.details.distance'),
                      dataIndex: 'distance_meters',
                      key: 'distance_meters',
                      render: (m: number | null) =>
                        m != null ? formatDistance(m) : '—',
                    },
                  ]}
                />
              </>
            )}

            {detailEvent.status !== 'resolved' && detailTrip && (
              <>
                <Divider orientation="left">{t('sos.details.dispatchRescue')}</Divider>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Select
                    placeholder={t('sos.details.selectVehicle')}
                    style={{ width: '100%' }}
                    value={dispatchVehicleId}
                    onChange={setDispatchVehicleId}
                    showSearch
                    optionFilterProp="label"
                    options={rescueVehicles.map((v) => ({
                      value: v.vehicle_id,
                      label: `${v.fleet_number} - ${v.model}`,
                    }))}
                  />
                  <Select
                    placeholder={t('sos.details.selectDriver')}
                    style={{ width: '100%' }}
                    value={dispatchDriverId}
                    onChange={setDispatchDriverId}
                    showSearch
                    optionFilterProp="label"
                    options={activeDrivers.map((d) => ({
                      value: d.id,
                      label: d.name,
                    }))}
                  />
                  <Button
                    type="primary"
                    danger
                    loading={dispatching}
                    disabled={!canHandle || !dispatchVehicleId || !dispatchDriverId}
                    onClick={() => void handleDispatchRescue()}
                  >
                    {t('sos.details.dispatchRescue')}
                  </Button>
                </Space>
              </>
            )}
          </>
        )}
      </EntityDrawer>
    </>
  );
}

export default SOSEvents;
